import {
  ArrowClockwiseIcon,
  CheckIcon,
  CopyIcon,
  DotsThreeIcon,
  PencilSimpleIcon,
  SidebarSimpleIcon,
  SpinnerGapIcon,
  XIcon,
} from '@phosphor-icons/react';
import React, { useCallback, useEffect } from 'react';
import { toast } from 'sonner';

import { DocumentBody as DocumentMarkdownBody } from '@/components/documents/DocumentBody';
import {
  DocumentEditorFeedback,
  documentEditorStatus,
} from '@/components/documents/DocumentEditorFeedback';
import { DocumentRichEditor } from '@/components/documents/DocumentRichEditor';
import { ArticleHandoffDialog } from '@/components/reach/editorial/ArticleHandoffDialog';
import { EditorialArticleStatus } from '@/components/reach/editorial/EditorialArticleStatus';
import { EditorialTopicDockBody } from '@/components/reach/editorial/EditorialTopicDockBody';
import {
  type ArticleHeaderAction,
  nextNoteKey,
} from '@/components/reach/editorial/editorial-article-header';
import { Button } from '@/components/ui/button';
import { CircleIconButton } from '@/components/ui/circle-icon-button';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { ContextStrip } from '@/components/ui/context-strip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { FloatingPanel, FloatingPanelBody } from '@/components/ui/floating-panel';
import { InspectorHeader } from '@/components/ui/inspector/InspectorHeader';
import { InspectorPropertyRow } from '@/components/ui/inspector/InspectorPropertyRow';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { usePromoteArticleRevision } from '@/hooks/reach/useArticleReviewJob';
import {
  articleCritiqueFindingKey,
  useEditorialArticleTrail,
} from '@/hooks/reach/useEditorialArticleTrail';
import { useAuth } from '@/hooks/use-auth';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBrandKits } from '@/hooks/useBrandKits';
import { useDocumentMarkdownEditor } from '@/hooks/useDocumentMarkdownEditor';
import { type ReviewHighlightNote, useReviewHighlights } from '@/hooks/useReviewHighlights';
import { decidedFindingKeys } from '@/lib/content-doctrine/article-critique-confirmation';
import { summariseFactCheck } from '@/lib/content-doctrine/article-factcheck';
import {
  type ArticleReleaseInput,
  isReleaseReady,
  releaseDisabledReason as releaseDisabledReasonFor,
} from '@/lib/content-doctrine/article-release';
import { exportDocx, exportMarkdown, exportPdf } from '@/lib/documents/export-document';
import { ARTIFACT_PREVIEW_STAGE } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { useDocumentDialogStore } from '@/stores/useDocumentDialogStore';

interface DocumentBodyProps {
  messageId: string;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
}

/**
 * Lifts DocumentDialog's body content into the Inspector. Reads the rich
 * payload from useDocumentDialogStore (carrier mirroring useImageDialogStore).
 *
 * The Inspector shell provides the full-frame workspace wrapper + close
 * gesture; this body renders only the title breadcrumb and markdown viewer.
 */

export const DocumentBody: React.FC<DocumentBodyProps> = ({ onClose, breadcrumbs }) => {
  const payload = useDocumentDialogStore((s) => s.payload);
  const onCopy = useDocumentDialogStore((s) => s.onCopy);
  const onDownload = useDocumentDialogStore((s) => s.onDownload);
  const openDocument = useDocumentDialogStore((s) => s.open);
  const { session } = useAuth();
  const { activeKit } = useBrandKits();
  const promote = usePromoteArticleRevision(activeKit?.id ?? null);
  const { confirm, ConfirmDialogElement } = useConfirmDialog();
  const reviewActionHandledRef = React.useRef<string | null>(null);
  const isMobile = useIsMobile();
  const hasReviewRail = Boolean(payload?.type === 'article' && payload.editorialTopicId);
  const [reviewRailOpen, setReviewRailOpen] = React.useState(hasReviewRail);
  const [handoffOpen, setHandoffOpen] = React.useState(false);

  React.useEffect(() => {
    // A review Sheet obscures the document on a phone, so entering an article
    // must start with the document visible. Desktop has enough room to open the
    // canonical right slot alongside it.
    setReviewRailOpen(hasReviewRail && !isMobile);
  }, [hasReviewRail, isMobile, payload?.id]);

  const editor = useDocumentMarkdownEditor({
    documentId: payload?.id,
    messageId: payload?.messageId,
    markdown: payload?.markdown ?? '',
    editorialTopicId: payload?.type === 'article' ? payload.editorialTopicId : null,
  });
  const editorialTrail = useEditorialArticleTrail(
    payload?.type === 'article' && payload.messageId ? payload.messageId : null,
  );
  const markdown = editor.markdown;
  const publishingMetadata = editorialTrail.data?.webMetadata ?? undefined;
  const critique = editorialTrail.data?.editorialCritique;
  const decidedKeys = critique
    ? decidedFindingKeys(
        critique.reviewedFindingKeys,
        critique.claimCheckConfirmations,
        critique.reviewerLimitKeys,
      )
    : null;
  const unresolvedCritique = critique
    ? critique.report.findings.filter(
        (finding) => !decidedKeys?.has(articleCritiqueFindingKey(finding)),
      ).length
    : 0;
  const releaseInput: ArticleReleaseInput = {
    webReadiness: editorialTrail.data?.webReadiness,
    critiqueCurrent: Boolean(critique?.isCurrent),
    critiqueStatus: critique?.report.status,
    unresolvedEditorialFindings: critique?.isCurrent ? unresolvedCritique : 0,
    languageFindings: editorialTrail.data?.editingPass?.findings.length ?? 0,
    factCheck: summariseFactCheck(editorialTrail.data?.factCheck ?? null),
  };
  const readerRef = React.useRef<HTMLDivElement>(null);
  const focusedFindingKey = useDocumentDialogStore((s) => s.focusedFindingKey);
  const focusRequestId = useDocumentDialogStore((s) => s.focusRequestId);
  const focusFinding = useDocumentDialogStore((s) => s.focusFinding);
  // Open notes only, and only while the review describes the article being shown: an older saved
  // version, or a review of a previous revision, would point at passages that are not there.
  const reviewNotes: ReviewHighlightNote[] =
    critique?.isCurrent && !payload?.historicalNotice
      ? critique.report.findings
          .filter((finding) => !decidedKeys?.has(articleCritiqueFindingKey(finding)))
          .map((finding) => ({
            key: articleCritiqueFindingKey(finding),
            quote: finding.quote,
            kind: finding.dimension === 'evidence' ? ('evidence' as const) : ('prose' as const),
          }))
      : [];
  useReviewHighlights({
    rootRef: readerRef,
    notes: reviewNotes,
    focusedKey: focusedFindingKey,
    focusRequestId,
    resetKey: payload?.id ?? null,
    // Text to rail: a press on a marked passage points the rail at its note. While editing, the
    // press is also placing the caret, so it never pops a closed rail open over the text.
    onPressNote: (key) => {
      focusFinding(key);
      if (!editor.isEditing) setReviewRailOpen(true);
    },
  });
  const handleHeaderAction = (action: ArticleHeaderAction) => {
    if (action.kind === 'hand-off') {
      setHandoffOpen(true);
      return;
    }
    const key = nextNoteKey(
      reviewNotes.map((note) => note.key),
      focusedFindingKey,
    );
    setReviewRailOpen(true);
    if (key) focusFinding(key);
  };
  const canRelease = isReleaseReady(releaseInput);
  const releaseDisabledReason = releaseDisabledReasonFor(releaseInput);

  // "Fix with AI" / "Edit passage" from the review rail while the article is being read: switch to
  // the editor. The editor itself then selects the passage and opens the bar once it has mounted
  // with the article loaded (DocumentRichEditor owns that, so it also works when already editing).
  React.useEffect(() => {
    const action = payload?.reviewAction;
    if (!action || editor.isEditing) return;
    const key = `${payload?.id ?? ''}:${action.quote}:${action.instruction ?? ''}:${action.requestId ?? 0}`;
    if (reviewActionHandledRef.current === key) return;
    if (!editor.canEdit) {
      if (editor.isLoadingArticle) return;
      reviewActionHandledRef.current = key;
      toast.error('This article cannot be edited here, so the passage was not opened.');
      return;
    }
    reviewActionHandledRef.current = key;
    editor.beginEdit();
  }, [editor, payload]);

  // An editable document opens in the editor: no separate Edit step (founder, 2026-10-18, after the
  // Claude Docs editor). A save or a discard ends the hook's editing session, so this starts the next
  // one from the saved text. Saves stay explicit: each one is a compare-and-swap write and, for an
  // editorial article, queues a recheck, so saving on every pause would run the checks per keystroke.
  // An older saved version stays read-only; editing it would write over the current article.
  const autoEdit = editor.canEdit && !payload?.historicalNotice;
  React.useEffect(() => {
    if (autoEdit && !editor.isEditing && !editor.isSaving) editor.beginEdit();
  }, [autoEdit, editor]);

  useEffect(() => {
    if (!payload || !editor.hasAuthoritativeContent || payload.markdown === markdown) return;
    // Saving an old reader must never replace a different document opened while
    // the request was in flight. Overrides may capture old prose, so retire them.
    useDocumentDialogStore.setState((state) =>
      state.payload?.id === payload.id && state.payload?.messageId === payload.messageId
        ? { payload: { ...state.payload, markdown }, onCopy: undefined, onDownload: undefined }
        : {},
    );
  }, [payload, markdown, editor.hasAuthoritativeContent]);

  // Design spec (docs/superpowers/specs/2026-04-22-documents-tab-design.md
  // §5.4) requires Copy + Download here — the legacy MarkdownCanvas had both,
  // but the DS-22 Inspector-consolidation refactor moved Documents onto this
  // surface without carrying them over. Neither current caller (DocumentsListView,
  // PlanContextSection) supplies onCopy/onDownload, so default to acting
  // directly on the payload rather than requiring every future caller to wire
  // the same boilerplate — onCopy/onDownload stay available as override hooks.
  const handleCopy = useCallback(async () => {
    if (onCopy) {
      await onCopy();
      return;
    }
    try {
      await navigator.clipboard.writeText(markdown);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  }, [onCopy, markdown]);

  /**
   * 🔴 THREE FORMATS, AND ALL THREE ALREADY EXISTED.
   *
   * This button wrote a hand-rolled `new Blob([markdown])` and nothing else,
   * so the only way out of the article reader was a `.md` file. Founder,
   * 2026-09-07: _"Is that article downloaded as Markdown? I guess that's fine,
   * but what about if I want to download it as a Word document? Does that
   * work?"_
   *
   * It did not — from here. `exportMarkdown`, `exportPdf` and `exportDocx`
   * have been in `lib/documents/export-document.ts` the whole time, `docx` and
   * `jspdf` are already dependencies, and the CHAT document chip has offered
   * all three for months. This surface simply never called them: a capability
   * built, shipped, and unreachable from the place a person actually reads the
   * article, which is this codebase's most frequent defect shape.
   *
   * `onDownload` stays an override for callers that supply their own.
   */
  /**
   * 🔴 A DOWNLOAD IS HOW AN ARTICLE ACTUALLY LEAVES MORA — founder, 2026-09-21:
   * six articles showing "Ready to hand off" were downloaded and sent onward,
   * and nothing in the Editorial table or Map moved, because a download has
   * never written anything: `exportDocx`/`exportPdf`/`exportMarkdown` build a
   * file client-side and touch no table. There is no fact to derive a status
   * from without asking — a silent "downloaded ⇒ handed off" write would be
   * exactly the kind of invented status the north-star's no-hallucination law
   * forbids, since a download does not say WHERE the piece went. So a
   * download of a reviewable article now opens the same `ArticleHandoffDialog`
   * used from the menu, pre-empted to the point of actually asking: the one
   * write that moves `external_publications` — and with it the Editorial
   * table's badge and filter (`editorialTopicStatus`) — is still a decision
   * the operator makes, just now prompted at the moment it is true, not left
   * for a menu item nobody returns to.
   */
  const nudgeHandoffAfterDownload = useCallback(() => {
    if (!hasReviewRail) return;
    setHandoffOpen(true);
    toast('Record where this article is going', {
      description: 'Downloading it doesn’t update its status here — the handoff dialog does.',
    });
  }, [hasReviewRail]);

  const handleDownloadMarkdown = useCallback(async () => {
    if (onDownload) {
      await onDownload();
      return;
    }
    if (!payload) return;
    exportMarkdown(markdown, payload.title || 'document', publishingMetadata);
    nudgeHandoffAfterDownload();
  }, [onDownload, payload, markdown, publishingMetadata, nudgeHandoffAfterDownload]);

  const handleDownloadPdf = useCallback(async () => {
    if (!payload) return;
    try {
      await exportPdf(markdown, payload.title || 'document', publishingMetadata);
      nudgeHandoffAfterDownload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not create PDF');
    }
  }, [payload, markdown, publishingMetadata, nudgeHandoffAfterDownload]);

  const handleDownloadDocx = useCallback(async () => {
    if (!payload) return;
    try {
      await exportDocx(markdown, payload.title || 'document', publishingMetadata);
      nudgeHandoffAfterDownload();
    } catch {
      toast.error('Could not create Word document');
    }
  }, [payload, markdown, publishingMetadata, nudgeHandoffAfterDownload]);

  if (!payload) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-background p-6">
        <Empty className="border-0">
          <EmptyHeader>
            <EmptyTitle>Document unavailable</EmptyTitle>
            <EmptyDescription>
              This file could not be loaded. Close the viewer and try opening it again.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  const publishingSection = publishingMetadata ? (
    <section
      className="mx-auto w-full max-w-196 px-5 pb-10 lg:px-8"
      data-review-highlight-ignore=""
      aria-label="CMS publishing details"
    >
      <p className="mb-2 text-detail font-medium text-foreground">CMS publishing details</p>
      <div className="border-y border-border/30">
        {publishingMetadata.metaTitle ? (
          <InspectorPropertyRow label="Title tag">
            <span className="break-words">{publishingMetadata.metaTitle}</span>
          </InspectorPropertyRow>
        ) : null}
        {publishingMetadata.metaDescription ? (
          <InspectorPropertyRow label="Meta description">
            <span className="break-words">{publishingMetadata.metaDescription}</span>
          </InspectorPropertyRow>
        ) : null}
        {publishingMetadata.slug ? (
          <InspectorPropertyRow label="Slug">
            <span className="break-all">/{publishingMetadata.slug}</span>
          </InspectorPropertyRow>
        ) : null}
      </div>
      <p className="mt-2 text-detail text-muted-foreground">
        Included in Word, PDF, and Markdown downloads for the publishing team.
      </p>
    </section>
  ) : null;

  return (
    <div className="flex h-full flex-col">
      <InspectorHeader
        title={payload.title}
        breadcrumbs={breadcrumbs}
        onClose={onClose}
        className="border-b border-border/40"
        meta={
          hasReviewRail && payload.editorialTopicId && editorialTrail.data ? (
            <EditorialArticleStatus
              topicId={payload.editorialTopicId}
              release={releaseInput}
              {...(editor.isDirty ? {} : { onAction: handleHeaderAction })}
            />
          ) : undefined
        }
        actions={
          <>
            {editor.isEditing ? (
              <CircleIconButton
                size="header"
                ariaLabel={editor.isSaving ? 'Saving document' : 'Save document'}
                tooltip={editor.isDirty ? 'Save' : 'No unsaved changes'}
                disabled={
                  editor.isSaving ||
                  !editor.isDirty ||
                  !editor.draft.trim() ||
                  editor.conflict ||
                  !editor.canEdit
                }
                onClick={() => void editor.save()}
              >
                {editor.isSaving ? (
                  <SpinnerGapIcon weight="regular" className="animate-spin" />
                ) : (
                  <CheckIcon weight="regular" />
                )}
              </CircleIconButton>
            ) : null}
            {hasReviewRail ? (
              <CircleIconButton
                size="header"
                ariaLabel={reviewRailOpen ? 'Hide article review' : 'Show article review'}
                tooltip={reviewRailOpen ? 'Hide review' : 'Show review'}
                aria-pressed={reviewRailOpen}
                onClick={() => setReviewRailOpen((open) => !open)}
              >
                <SidebarSimpleIcon weight="regular" />
              </CircleIconButton>
            ) : !editor.isEditing && editor.canEdit ? (
              <CircleIconButton
                size="header"
                ariaLabel="Edit document"
                tooltip="Edit"
                onClick={editor.beginEdit}
              >
                <PencilSimpleIcon weight="regular" />
              </CircleIconButton>
            ) : null}
            {markdown.trim().length > 0 && !editor.isLoadingArticle ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <CircleIconButton
                    size="header"
                    ariaLabel="Document actions"
                    tooltip="Copy or download"
                    disabled={editor.isSaving}
                  >
                    <DotsThreeIcon weight="regular" />
                  </CircleIconButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {hasReviewRail ? (
                    <>
                      <DropdownMenuItem onSelect={() => setHandoffOpen(true)}>
                        Hand off article
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {hasReviewRail && !editor.isEditing && editor.canEdit ? (
                    <>
                      <DropdownMenuItem onSelect={editor.beginEdit}>
                        <PencilSimpleIcon weight="regular" />
                        Edit document
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  {editor.isEditing && editor.isDirty ? (
                    <>
                      <DropdownMenuItem onSelect={editor.cancelEdit}>
                        <XIcon weight="regular" />
                        Discard changes
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  <DropdownMenuItem onSelect={() => void handleCopy()}>
                    <CopyIcon weight="regular" />
                    Copy document
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void handleDownloadDocx()}>
                    Download Word (.docx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleDownloadPdf()}>
                    Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void handleDownloadMarkdown()}>
                    Download Markdown (.md)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        }
      />
      {/* `bg-canvas` (ARTIFACT_PREVIEW_STAGE) — the same "quiet table" tone
          PostBody's preview pane and MediaStudioAssetStage use, so a document
          reads as content sitting on its own surface instead of flush with
          the header chrome above it (added 2026-09-06; this body previously
          had no stage tone at all — flat against the ambient background).
          It lives on the stage WRAPPER, not the document pane, so the review
          rail's gutter shares the one surface: when only the document pane
          carried it, the rail's backdrop fell through to `bg-background`
          (252,252,252 against the stage's 249,248,246) and the card read as
          floating on a second, brighter surface. */}
      <div className={cn('relative flex min-h-0 flex-1', ARTIFACT_PREVIEW_STAGE)}>
        <ResizablePanelGroup className="min-h-0 flex-1">
          <ResizablePanel
            defaultSize={reviewRailOpen && !isMobile ? '70' : '100'}
            minSize="45"
            className="min-w-0"
          >
            <div ref={readerRef} className="h-full min-w-0 overflow-y-auto px-5 pb-8">
              {/* A scope band, not an alert. Which version you are reading is a
                  standing fact about this view, so it reads as one quiet line
                  with the way back beside it — an `Alert` rendered a 766×54
                  bordered box of mostly empty space above the headline. */}
              {payload.historicalNotice ? (
                <ContextStrip
                  role="status"
                  split
                  data-review-highlight-ignore=""
                  className="mx-auto mt-4 max-w-180"
                >
                  <p className="text-caption text-foreground">{payload.historicalNotice}</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {payload.currentVersion ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const current = payload.currentVersion;
                          if (current) openDocument({ document: current });
                        }}
                      >
                        Back to current
                      </Button>
                    ) : null}
                    {payload.promotable && payload.editorialTopicId && payload.messageId ? (
                      <Button
                        variant="neutral"
                        size="sm"
                        disabled={promote.isPending}
                        onClick={() => {
                          const topicId = payload.editorialTopicId!;
                          const messageId = payload.messageId!;
                          const documentId = payload.id;
                          const title = payload.currentVersion?.title ?? payload.title;
                          confirm({
                            title: 'Use this version as the article?',
                            description:
                              'It replaces the current article and goes back to review, so its checks and any approval apply to this version. Earlier saved versions stay in the list.',
                            confirmLabel: 'Use this version',
                            onConfirm: async () => {
                              try {
                                await promote.mutateAsync({ topicId, messageId, documentId });
                                openDocument({
                                  document: {
                                    ...payload,
                                    title,
                                    historicalNotice: undefined,
                                    currentVersion: undefined,
                                    promotable: false,
                                  },
                                });
                                toast.success('This version is now the article.');
                              } catch (error) {
                                toast.error(
                                  error instanceof Error
                                    ? error.message
                                    : 'This version could not be made current.',
                                );
                              }
                            },
                          });
                        }}
                      >
                        {promote.isPending ? 'Switching…' : 'Use this version'}
                      </Button>
                    ) : null}
                  </div>
                </ContextStrip>
              ) : null}
              {editor.isLoadingArticle ? (
                <p role="status" className="pt-4 text-caption text-muted-foreground">
                  Loading saved article…
                </p>
              ) : null}
              {editor.loadError ? (
                <div className="flex flex-col items-start gap-2 py-4">
                  <p role="alert" className="text-caption text-destructive">
                    {editor.loadError}
                  </p>
                  <CircleIconButton
                    size="header"
                    ariaLabel="Reload saved version"
                    tooltip="Reload saved version"
                    onClick={() => void editor.reloadSavedVersion()}
                  >
                    <ArrowClockwiseIcon weight="regular" />
                  </CircleIconButton>
                </div>
              ) : null}
              {editor.isEditing || autoEdit ? (
                // The toolbar spans the pane edge to edge; the article keeps
                // the reader's 720px measure inside `DocumentRichEditor`.
                // `autoEdit` keeps the editor mounted through the one render
                // between a save and the next editing session, so the caret and
                // scroll position survive a save.
                <div className="flex w-full flex-col pb-16">
                  <DocumentRichEditor
                    markdown={editor.isEditing ? editor.draft : markdown}
                    onChange={editor.setDraft}
                    disabled={editor.isSaving}
                    accessToken={session?.access_token ?? null}
                    reviewAction={payload.reviewAction}
                    status={documentEditorStatus(editor)}
                    feedback={<DocumentEditorFeedback editor={editor} showStatus={false} />}
                  />
                  {publishingSection}
                </div>
              ) : markdown.trim().length > 0 ? (
                <>
                  <DocumentMarkdownBody markdown={markdown} presentation="workspace" />
                  {publishingSection}
                </>
              ) : editor.loadError || editor.isLoadingArticle ? null : (
                <div className="flex min-h-full items-center justify-center py-12">
                  <Empty className="border-0">
                    <EmptyHeader>
                      <EmptyTitle>No document content</EmptyTitle>
                      <EmptyDescription>
                        Mora found the file record, but there is no readable content to display.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                </div>
              )}
            </div>
          </ResizablePanel>
          {hasReviewRail && reviewRailOpen && !isMobile && payload.editorialTopicId ? (
            <>
              {/* Docked, not floating: one hairline divides the article from its
                  review, both on the same stage tone, the way a document editor
                  docks its comments. The elevated card with an 8px gutter read as
                  a second window parked beside the article (founder, 2026-10-17). */}
              <ResizableHandle withHandle />
              <ResizablePanel
                defaultSize="30"
                minSize="320px"
                maxSize="50"
                className="min-w-0 overflow-hidden"
                data-right-slot-pane=""
              >
                <FloatingPanel
                  surface="flush"
                  aria-label="Article review"
                  className="min-w-0 max-w-full bg-transparent"
                >
                  <FloatingPanelBody className="min-w-0 overflow-x-hidden">
                    <EditorialTopicDockBody topicId={payload.editorialTopicId} variant="document" />
                  </FloatingPanelBody>
                </FloatingPanel>
              </ResizablePanel>
            </>
          ) : null}
        </ResizablePanelGroup>
        {hasReviewRail && isMobile && payload.editorialTopicId ? (
          <Sheet open={reviewRailOpen} onOpenChange={setReviewRailOpen}>
            <SheetContent side="right" className="w-full max-w-full p-0 sm:max-w-md">
              <SheetTitle className="sr-only">Article review</SheetTitle>
              <FloatingPanel surface="flush" aria-label="Article review">
                <FloatingPanelBody className="min-w-0 overflow-x-hidden">
                  <EditorialTopicDockBody topicId={payload.editorialTopicId} variant="document" />
                </FloatingPanelBody>
              </FloatingPanel>
            </SheetContent>
          </Sheet>
        ) : null}
      </div>
      {hasReviewRail &&
      activeKit?.id &&
      payload.editorialTopicId &&
      payload.messageId &&
      editorialTrail.data?.documentId &&
      editorialTrail.data.contentHash ? (
        <ArticleHandoffDialog
          open={handoffOpen}
          onOpenChange={setHandoffOpen}
          workspaceId={activeKit.id}
          taskId={payload.editorialTopicId}
          title={editorialTrail.data.title || payload.title}
          markdown={markdown}
          messageId={payload.messageId}
          documentId={editorialTrail.data.documentId}
          contentHash={editorialTrail.data.contentHash}
          canRelease={canRelease}
          releaseDisabledReason={releaseDisabledReason}
        />
      ) : null}
      {ConfirmDialogElement}
    </div>
  );
};
